import { useAtom } from 'jotai';
import { useMemo } from 'react';

import { PRIORITY_COLORS } from '@/constants/chartcolors.constants';
import { PRIORITY } from '@/constants/common';
import userAtom from '@/store/user.store';

import { recentAnnouncementsAtom } from '../Announcements/Announcements.store';
import { orgProductsAtom, selectedOrganizationAtom } from '../Organizations';
import {
  useFetchGroupByOptions,
  useFetchV2Tickets
} from '../Tickets/Tickets.store';
import dashboardTimeFrameAtom, {
  alertsByDateAtom,
  basicSearchParamsAtom,
  eventsByDateAtom,
  logUsageByDateAtom,
  logUsageLimitsAtom,
  networkUsageByDateAtom,
  networkUsageLimitsAtom,
  ticketSearchParamsAtom,
  usageAverageAtom
} from './Dashboard.store';
import {
  DASHBOARD_TICKET_GROUP_BY_FIELDS,
  OPEN_TICKET_STATUS,
  TICKET_QUERY_PARAMS
} from './dashboard.constants';

const useDashboard = () => {
  const [user] = useAtom(userAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [eventsByDate] = useAtom(eventsByDateAtom);
  const [alertsByDate] = useAtom(alertsByDateAtom);
  const [dashboardTimeFrame] = useAtom(dashboardTimeFrameAtom);
  const [usageAverages] = useAtom(usageAverageAtom);
  const [logUsageLimit] = useAtom(logUsageLimitsAtom);
  const [networkUsageLimit] = useAtom(networkUsageLimitsAtom);
  const [logUsageByDate] = useAtom(logUsageByDateAtom);
  const [networkUsageByDate] = useAtom(networkUsageByDateAtom);
  const [recentAnnouncements] = useAtom(recentAnnouncementsAtom);
  const [orgProducts] = useAtom(orgProductsAtom);
  const [basicSearchParams] = useAtom(basicSearchParamsAtom);
  const [ticketSearchParams] = useAtom(ticketSearchParamsAtom);

  // ------- Tickets by priority and status

  const { data: groupByOptions, isFetching: isFetchingGroupByOptions } =
    useFetchGroupByOptions(DASHBOARD_TICKET_GROUP_BY_FIELDS, basicSearchParams);

  const percentageBarData = useMemo(() => {
    return Object.entries(PRIORITY)?.map(([key, value]) => {
      const count =
        groupByOptions?.ticket_priority__name?.find(
          (option) => option.label === value
        )?.count || 0;

      return {
        name: value,
        type: 'count',
        data: [count],
        color: PRIORITY_COLORS[key as keyof typeof PRIORITY_COLORS] || '#008ffb'
      };
    });
  }, [groupByOptions]);

  // --------

  // All tickets for the table
  const { data: tickets } = useFetchV2Tickets({
    enabled: true,
    queryParams: TICKET_QUERY_PARAMS,
    searchParams: ticketSearchParams
  });

  // -----

  // Get and calculate values for small cards
  const highPriorityTicketsSearchParams = useMemo(() => {
    return {
      ...basicSearchParams,
      ticket_status__name__in: OPEN_TICKET_STATUS,
      ticket_priority__name__in: ['P1', 'P2']
    };
  }, [basicSearchParams]);

  const { data: highPriorityThreatTicketsGroupBy } = useFetchGroupByOptions(
    DASHBOARD_TICKET_GROUP_BY_FIELDS,
    highPriorityTicketsSearchParams
  );

  const highPriorityTicketsCount = useMemo(() => {
    return Object.values(
      highPriorityThreatTicketsGroupBy?.ticket_priority__name || {}
    )
      .map((value) => {
        const count = value.count || 0;
        return count;
      })
      .reduce((acc, count) => acc + count, 0);
  }, [highPriorityThreatTicketsGroupBy]);

  const openTicketsCount = useMemo(() => {
    return (
      groupByOptions?.ticket_status__name?.find(
        (option) => option.label === 'Open'
      )?.count || 0
    );
  }, [groupByOptions]);

  const acknowledgedTicketsCount = useMemo(() => {
    return (
      groupByOptions?.ticket_status__name?.find(
        (option) => option.label === 'Acknowledged'
      )?.count || 0
    );
  }, [groupByOptions]);

  const unresolvedTicketsCount = useMemo(() => {
    return (openTicketsCount || 0) + (acknowledgedTicketsCount || 0);
  }, [openTicketsCount, acknowledgedTicketsCount]);

  // -------

  return {
    highPriorityTicketsCount,
    unresolvedTicketsCount,
    openTicketsCount,
    tickets,
    eventsByDate,
    alertsByDate,
    dashboardTimeFrame,
    usageAverages,
    logUsageLimit,
    networkUsageLimit,
    orgProducts,
    logUsageByDate,
    networkUsageByDate,
    user,
    selectedOrganization,
    recentAnnouncements,
    percentageBarData,
    isFetchingGroupByOptions
  };
};

export default useDashboard;
